import React, { useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { SmallSelect } from '@/components/small-select';
import { Flexbox } from '@/components/flex';
import {
  OrButton,
  QBCaption,
  QBRemove,
  QBVSpace,
} from '@/components/query-builder/shared';
import { QBContext } from '@/components/query-builder/context';
import { arrayValueOptions } from '@/components/query-builder/array-value';
import { HSpace } from '@/components/spacing';
import { TextInput } from '@/components/text-input';
import { TrackElementContext } from '@/features/editor/widgets/track-element/context';

export function TrackElementValue({ idx }: { idx: number }) {
  const { queryState, appendValue, updateValue } = useContext(QBContext);
  const { setFocusedInput } = useContext(TrackElementContext);
  const current = queryState[idx];

  const onOpChanged = (childIdx, op) => {
    updateValue(idx, childIdx, op, current?.values?.[childIdx]?.value);
  };

  const onAddMore = () => {
    appendValue(idx, current?.values?.[0]?.value || `click`, ``);
    setFocusedInput(idx, current.values.length);
  };

  useEffect(() => {
    const result = [];
    current.values.forEach((v, i) => {
      result[i] = current?.qbProps?.options?.find((o) => o.key === v?.value);
    });
  }, [current?.values]);

  return (
    <Wrapper>
      {current?.values?.map((_, childIdx) => (
        <React.Fragment key={`arrayidxx-${childIdx}`}>
          <Row>
            {childIdx > 0 && <LogicalOr>Or</LogicalOr>}
            {childIdx === 0 && (
              <SmallSelect
                minWidth={70}
                options={
                  current?.qbProps?.arrayValueOptions ?? arrayValueOptions
                }
                onSelection={(selected) => onOpChanged(childIdx, selected)}
                defaultValue={current?.values?.[childIdx]?.op}
              />
            )}
            <HSpace value={childIdx === 0 ? 2 : 12} />
            <QbSelectorInput idx={idx} childIdx={childIdx} />
          </Row>
          <QBVSpace />
        </React.Fragment>
      ))}
      <OrButton onClick={onAddMore}>|| Or</OrButton>
    </Wrapper>
  );
}
const InputWrapper = styled.div`
  position: relative;
`;
const FocusIconContainer = styled.svg``;
const FocusIconWrapper = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;
  :hover {
    cursor: pointer;
  }

  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  cursor: default;
`;
const QBTextFocusableInput = styled(TextInput)`
  && {
    width: 25rem;
    text-align: start;
    border-radius: 2rem;
    font-size: 1.4rem;
    font-family: 'JetBrains Mono', serif;
    box-shadow: none;
    padding: 0.55rem 3rem 0.55rem 2rem;
    border: 1.5px solid ${(p) => (p.isFocused ? `blue` : `#e8e8ea`)};
  }
`;
const Wrapper = styled(Flexbox)`
  && {
    flex-direction: column;
  }
`;

const Row = styled(Flexbox)`
  && {
    position: relative;
    flex-direction: row;
  }
`;

const LogicalOr = styled(QBCaption)`
  && {
    position: absolute;
    top: calc(50% - 1rem);
    left: -4rem;
  }
`;

function FocusedIcon({ isFocused }: { isFocused?: boolean }) {
  const colorPath = isFocused ? `blue` : ``;
  return (
    <FocusIconContainer
      width="17px"
      height="16px"
      viewBox="0 0 17 16"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        id="Page-1"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
      >
        <g
          id="Clarity-integration---smart-rule"
          transform="translate(-1645, -512)"
          stroke={isFocused ? `blue` : `#000000`}
          strokeWidth="1.3243176"
        >
          <g id="Group-15" transform="translate(1192.9834, 502)">
            <g id="Group-6" transform="translate(453.1036, 10.6997)">
              <path
                d="M8.69512313,5.45251433 L12.2528658,5.45251433 C13.223955,5.45251433 14.1212783,4.93289268 14.6068229,4.08938577 C15.0923675,3.24587886 15.0923675,2.20663548 14.6068229,1.36312857 C14.1212783,0.519621656 13.223955,0 12.2528658,0 L2.71811538,0 C1.2169417,0 0,1.22058693 0,2.72625717 C0,4.2319274 1.2169417,5.45251433 2.71811538,5.45251433 L5.22276625,5.45251433"
                id="Path"
              />
              <polygon
                fill={colorPath}
                id="Path"
                points="14.5440522 11.9826853 11.4132386 8.8424938 14.0886611 7.32235564 4.75314421 4.08938575 7.97645911 13.4528659 9.4920575 10.7694295 12.6228711 13.909621"
              />
            </g>
          </g>
        </g>
      </g>
    </FocusIconContainer>
  );
}

const useAutoFocus = (isFocused: boolean) => {
  const ref = useRef<HTMLInputElement>();
  useEffect(() => {
    if (isFocused) {
      const input = ref.current;
      input?.focus();
    }
  }, [isFocused]);
  return ref;
};

function QbSelectorInput({ childIdx, idx }: { idx: number; childIdx: number }) {
  const { queryState, updateValue, removeKey, removeValue } =
    useContext(QBContext);
  const current = queryState[idx];
  const onValueChanged = (childIdx, ev) => {
    updateValue(
      idx,
      childIdx,
      current?.values?.[childIdx]?.op,
      ev.target.value,
    );
  };

  const { focusInputX, focusInputY, setFocusedInput } =
    useContext(TrackElementContext);
  const onRemove = (childIdx: number) => {
    if (current.values.length === 1) {
      removeKey(idx);
    } else {
      removeValue(idx, childIdx);
    }
  };

  const isFocused = idx === focusInputX && childIdx === focusInputY;
  const ref = useAutoFocus(isFocused);

  return (
    <>
      <InputWrapper>
        <QBTextFocusableInput
          ref={ref}
          isFocused={isFocused}
          onClick={() => setFocusedInput(idx, childIdx)}
          value={current?.values?.[childIdx]?.value}
          onChange={(e) => onValueChanged(childIdx, e)}
        />
        <FocusIconWrapper onClick={() => setFocusedInput(idx, childIdx)}>
          <FocusedIcon isFocused={isFocused} />
          <IconTooltip focused={isFocused} />
        </FocusIconWrapper>
      </InputWrapper>
      <QBRemove
        shouldHide={current?.values?.length === 1 && queryState?.length === 1}
        onClick={() => onRemove(childIdx)}
      />
    </>
  );
}
const TooltipBubble = styled.div`
  position: absolute;
  bottom: -250%;
  left: -450%;
  transform: translateX(-50%);
  background: rgba(17, 17, 17, 0.95);
  color: #fff;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: opacity 120ms ease, visibility 120ms ease;
  z-index: 2147483841;

  ${FocusIconWrapper}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

function IconTooltip({ focused }: { focused: boolean }) {
  return (
    <TooltipBubble role="tooltip">
      {focused ? (
        <span>
          Click the editor <br />
          to choose an element
        </span>
      ) : (
        <span>
          Select, then click <br />
          the editor to <br /> choose an element
        </span>
      )}
    </TooltipBubble>
  );
}
